import { denormalize } from 'normalizr';
import { createSelector } from 'reselect';
import { orgStakeholder as orgStakeholderSchema } from '../lib/schema.js';
import { CALL_API, HTTP_METHODS } from '../middlewares/api.js';
import { deleteEntities, selectEntities } from './entities.js';
import { selectCurrentOrganizationId } from './auth.js';

// API Actions

const RETRIEVE_ORG_STAKEHOLDERS_REQUEST =
  'changesync/orgStakeholders/RETRIEVE_ORG_STAKEHOLDERS_REQUEST';
const RETRIEVE_ORG_STAKEHOLDERS_SUCCESS =
  'changesync/orgStakeholders/RETRIEVE_ORG_STAKEHOLDERS_SUCCESS';
const RETRIEVE_ORG_STAKEHOLDERS_FAILURE =
  'changesync/orgStakeholders/RETRIEVE_ORG_STAKEHOLDERS_FAILURE';
const SEARCH_ORG_STAKEHOLDERS_REQUEST =
  'changesync/orgStakeholders/SEARCH_ORG_STAKEHOLDERS_REQUEST';
const SEARCH_ORG_STAKEHOLDERS_SUCCESS =
  'changesync/orgStakeholders/SEARCH_ORG_STAKEHOLDERS_SUCCESS';
const SEARCH_ORG_STAKEHOLDERS_FAILURE =
  'changesync/orgStakeholders/SEARCH_ORG_STAKEHOLDERS_FAILURE';
const UPDATE_ORG_STAKEHOLDERS_REQUEST =
  'changesync/orgStakeholders/UPDATE_ORG_STAKEHOLDERS_REQUEST';
const UPDATE_ORG_STAKEHOLDERS_SUCCESS =
  'changesync/orgStakeholders/UPDATE_ORG_STAKEHOLDERS_SUCCESS';
const UPDATE_ORG_STAKEHOLDERS_FAILURE =
  'changesync/orgStakeholders/UPDATE_ORG_STAKEHOLDERS_FAILURE';
const CREATE_ORG_STAKEHOLDER_REQUEST =
  'changesync/orgStakeholders/CREATE_ORG_STAKEHOLDER_REQUEST';
const CREATE_ORG_STAKEHOLDER_SUCCESS =
  'changesync/orgStakeholders/CREATE_ORG_STAKEHOLDER_SUCCESS';
const CREATE_ORG_STAKEHOLDER_FAILURE =
  'changesync/orgStakeholders/CREATE_ORG_STAKEHOLDER_FAILURE';
// Search Actions
const UPDATE_SEARCH_QUERY = 'changesync/orgStakeholders/UPDATE_SEARCH_QUERY';
const SEARCH_ORG_STAKEHOLDERS =
  'changesync/orgStakeholders/SEARCH_ORG_STAKEHOLDERS';
// Initial State

const initialState = {
  retrieveOrgStakeholders: {
    stakeholdersIds: [],
    pending: false,
    success: false,
    error: null,
  },

  searchOrgStakeholders: {
    stakeholdersIds: [],
    pending: false,
    success: false,
    error: null,
    query: '',
  },
};

// Reducer

export default (state = initialState, action) => {
  switch (action.type) {
    case RETRIEVE_ORG_STAKEHOLDERS_REQUEST:
      return {
        ...state,
        retrieveOrgStakeholders: {
          ...state.retrieveOrgStakeholders,
          pending: true,
          success: false,
          error: null,
        },
      };
    case RETRIEVE_ORG_STAKEHOLDERS_SUCCESS:
      return {
        ...state,
        retrieveOrgStakeholders: {
          ...state.retrieveOrgStakeholders,
          stakeholdersIds: action.payload.result,
          pending: false,
          success: true,
        },
      };
    case RETRIEVE_ORG_STAKEHOLDERS_FAILURE:
      return {
        ...state,
        retrieveOrgStakeholders: {
          ...state.retrieveOrgStakeholders,
          pending: false,
          error: action.error,
        },
      };

    case CREATE_ORG_STAKEHOLDER_SUCCESS:
      return {
        ...state,
        retrieveOrgStakeholders: {
          ...state.retrieveOrgStakeholders,
          stakeholdersIds: [
            ...state.retrieveOrgStakeholders.stakeholdersIds,
            action.payload.result,
          ],
          pending: false,
          success: true,
        },
      };

    case SEARCH_ORG_STAKEHOLDERS_REQUEST:
      return {
        ...state,
        searchOrgStakeholders: {
          ...state.searchOrgStakeholders,
          pending: true,
          success: false,
          error: null,
        },
      };

    case SEARCH_ORG_STAKEHOLDERS_SUCCESS:
      return {
        ...state,
        searchOrgStakeholders: {
          ...state.searchOrgStakeholders,
          stakeholdersIds: action.payload.result,
          query: action.meta.query,
          pending: false,
          success: true,
        },
      };

    case UPDATE_ORG_STAKEHOLDERS_SUCCESS:
      return {
        ...state,
        searchOrgStakeholders: {
          ...state.searchOrgStakeholders,
          stakeholdersIds: [],
        },
        retrieveOrgStakeholders: {
          ...state.retrieveOrgStakeholders,
          stakeholdersIds: [],
        },
      };

    case UPDATE_SEARCH_QUERY:
      return {
        ...state,
        searchQuery: action.payload,
      };

    default:
      return state;
  }
};

// Action Creators
export const retrieveOrgStakeholders = (isReports) => (dispatch, getState) => {
  const organizationId = selectCurrentOrganizationId(getState());
  const param = isReports ? '?page=reports' : '';
  return dispatch({
    [CALL_API]: {
      types: [
        RETRIEVE_ORG_STAKEHOLDERS_REQUEST,
        RETRIEVE_ORG_STAKEHOLDERS_SUCCESS,
        RETRIEVE_ORG_STAKEHOLDERS_FAILURE,
      ],
      endpoint: `organizations/${organizationId}/organization_stakeholders${param}`,
      method: HTTP_METHODS.GET,
      schema: [orgStakeholderSchema],
    },
  });
};

export const searchOrgStakeholders = (query) => (dispatch, getState) => {
  const organizationId = selectCurrentOrganizationId(getState());

  const searchParam = query ? `?search=${query}` : '';

  return dispatch({
    [CALL_API]: {
      types: [
        SEARCH_ORG_STAKEHOLDERS_REQUEST,
        SEARCH_ORG_STAKEHOLDERS_SUCCESS,
        SEARCH_ORG_STAKEHOLDERS_FAILURE,
      ],
      endpoint: `organizations/${organizationId}/organization_stakeholders${searchParam}`,
      method: HTTP_METHODS.GET,
      schema: [orgStakeholderSchema],
    },
    meta: {
      debounce: {
        time: 400,
        key: SEARCH_ORG_STAKEHOLDERS,
      },
    },
  });
};

export const updateSearchQuery = (query) => ({
  type: UPDATE_SEARCH_QUERY,
  payload: query,
});

export const createOrgStakeholder =
  (orgStakeholder) => (dispatch, getState) => {
    const organizationId = selectCurrentOrganizationId(getState());
    return dispatch({
      [CALL_API]: {
        types: [
          CREATE_ORG_STAKEHOLDER_REQUEST,
          CREATE_ORG_STAKEHOLDER_SUCCESS,
          CREATE_ORG_STAKEHOLDER_FAILURE,
        ],
        endpoint: `organizations/${organizationId}/organization_stakeholders`,
        method: HTTP_METHODS.POST,
        schema: orgStakeholderSchema,
        body: orgStakeholder,
      },
    });
  };

export const updateOrgStakeholders =
  (orgStakeholders) => async (dispatch, getState) => {
    const organizationId = selectCurrentOrganizationId(getState());
    const entitiesToBeCleaned = Object.keys(
      getState().entities.orgStakeholders
    ).map((key) => `orgStakeholders.${key}`);

    await dispatch({
      [CALL_API]: {
        types: [
          UPDATE_ORG_STAKEHOLDERS_REQUEST,
          UPDATE_ORG_STAKEHOLDERS_SUCCESS,
          UPDATE_ORG_STAKEHOLDERS_FAILURE,
        ],
        endpoint: `organizations/${organizationId}/organization_stakeholders/update_structure`,
        method: HTTP_METHODS.PUT,
        schema: [orgStakeholderSchema],
        body: orgStakeholders,
      },
    });

    return dispatch(deleteEntities([...entitiesToBeCleaned]));
  };

export const selectRetrieveOrgStakeholdersState = createSelector(
  (state) => state.orgStakeholders,
  (orgStakeholders) => orgStakeholders.retrieveOrgStakeholders
);

export const selectSearchOrgStakeholdersState = createSelector(
  (state) => state.orgStakeholders,
  (orgStakeholders) => orgStakeholders.searchOrgStakeholders
);

const selectOrgStakeholderIds = createSelector(
  selectRetrieveOrgStakeholdersState,
  (state) => state.stakeholdersIds
);

export const selectOrgStakeholders = createSelector(
  selectOrgStakeholderIds,
  selectEntities,
  (stakeholdersIds, entities) => {
    const result = denormalize(
      stakeholdersIds,
      [orgStakeholderSchema],
      entities
    ).filter(({ archived }) => !archived);

    return result;
  }
);

export const selectOrgStakeholdersWithArchived = createSelector(
  selectOrgStakeholderIds,
  selectEntities,
  (stakeholdersIds, entities) =>
    denormalize(stakeholdersIds, [orgStakeholderSchema], entities)
);

const selectSearchOrgStakeholderIds = createSelector(
  selectSearchOrgStakeholdersState,
  (state) => state.stakeholdersIds
);

export const selectSearchOrgStakeholderResults = createSelector(
  selectSearchOrgStakeholderIds,
  selectEntities,
  (stakeholdersIds, entities) => {
    const result = denormalize(
      stakeholdersIds,
      [orgStakeholderSchema],
      entities
    ).filter(({ archived }) => !archived);

    return result;
  }
);
